import type { StreamHealth } from '../api/client';

export type ConnectionStatus = 'connected' | 'degraded' | 'disconnected';

/** Resolves connection_status with fallback to is_connected boolean. */
export function resolveStatus(connectionStatus?: string, isConnected?: boolean): ConnectionStatus {
  if (connectionStatus === 'connected' || connectionStatus === 'degraded' || connectionStatus === 'disconnected') {
    return connectionStatus;
  }
  return isConnected ? 'connected' : 'disconnected';
}

/** Returns the Tailwind dot color class for a connection status. */
export function getStatusDotClass(status?: string): string {
  if (status === 'connected') return 'bg-green-500';
  if (status === 'degraded') return 'bg-amber-500';
  return 'bg-gray-400';
}

/** Returns the human-readable label for a connection status. */
export function getConnectionLabel(status?: string): string {
  if (status === 'connected') return 'Connected';
  if (status === 'degraded') return 'Degraded';
  return 'Disconnected';
}

export interface ClusterCapability {
  label: string;
  online: boolean;
}

/**
 * Maps stream health to user-facing platform capabilities.
 *
 * Mapping:
 *   resource_ingestion → data stream
 *   incident_response  → events OR control stream
 *   flow_ingestion     → flows stream
 *   chat               → control stream
 *   vulnerability_scanning → control stream
 *
 * @param connectionStatus - if "disconnected", all capabilities are offline
 *   regardless of stream_health (stale data).
 */
export function getClusterCapabilities(streamHealth?: StreamHealth, connectionStatus?: string): ClusterCapability[] {
  if (!streamHealth || Object.keys(streamHealth).length === 0) {
    // No stream health data — assume all online (old operator or no data yet)
    const online = connectionStatus !== 'disconnected';
    return [
      { label: 'Resource Ingestion', online },
      { label: 'Incident Response', online },
      { label: 'Flow Ingestion', online },
      { label: 'Chat', online },
    ];
  }

  // If disconnected, stream_health is stale — everything is offline.
  // We still use the map keys to determine which capabilities are applicable.
  const isDisconnected = connectionStatus === 'disconnected';

  // Stream keys present in the map → report online/offline.
  // Stream keys ABSENT from the map → not applicable (operator doesn't use that stream).
  const data = isDisconnected ? false : streamHealth.data === true;
  const control = isDisconnected ? false : streamHealth.control === true;
  const events = isDisconnected ? false : streamHealth.events === true;
  const flows = isDisconnected ? false : streamHealth.flows === true;

  const caps: ClusterCapability[] = [
    { label: 'Resource Ingestion', online: data },
  ];

  // Incident Response needs events OR control — show if either key exists
  if ('events' in streamHealth || 'control' in streamHealth) {
    caps.push({ label: 'Incident Response', online: events || control });
  }

  // Flow Ingestion — only show if operator has flows stream
  if ('flows' in streamHealth) {
    caps.push({ label: 'Flow Ingestion', online: flows });
  }

  // Chat needs control stream
  caps.push({ label: 'Chat', online: control });

  return caps;
}
